import {
  synchronyDateFormat,
  IFinancingOfferFormatted,
  IFinancingOfferCampaign,
  ICampaignRemainingCost,
} from '~/interfaces/synchrony/synchrony-interfaces';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import {
  //   TODO:: IN STORE Offers - LIMITED
  //   LimitedTimeFinancingOffer,
  //   LimitedTimeFinancingOfferViewModel_SpecialCampaignOffer,
  Maybe,
} from '~/gql/__generated__/types';
import { parse, format } from 'date-fns';

dayjs.extend(tz);
dayjs.extend(utc);
dayjs.extend(isBetween);
export const DEFAULT_NSP_TIMEZONE = 'America/Chicago';

export function filterOfferWithLowestMaxPurchase<
  T extends { maxPurchase: number | null; offerName?: string | null },
>(
  offer: T,
  validOffers: T[],
  cartTotalPrice: number,
  lowCostOfferName: string | null,
): boolean {
  const currOfferMaxPurchase = offer.maxPurchase ?? 0;
  return (
    cartTotalPrice <= currOfferMaxPurchase &&
    offer.offerName?.toLowerCase() === lowCostOfferName?.toLowerCase() &&
    !validOffers.some(
      (otherOffer) =>
        otherOffer.maxPurchase &&
        otherOffer.maxPurchase < currOfferMaxPurchase &&
        otherOffer.maxPurchase >= cartTotalPrice,
    )
  );
}

export function filterOfferWithHighestMinPurchase<
  T extends { minPurchase: number | null },
>(offer: T, validOffers: T[], cartTotalPrice: number): boolean {
  const currOfferMinPurchase = offer.minPurchase ?? 0;

  return (
    cartTotalPrice >= currOfferMinPurchase &&
    !validOffers.some(
      (otherOffer) =>
        otherOffer.minPurchase &&
        otherOffer.minPurchase > currOfferMinPurchase &&
        otherOffer.minPurchase <= cartTotalPrice,
    )
  );
}

export function calculateFinancing(
  cartTotal: number,
  promoMonths: number,
  campaign:
    | IFinancingOfferCampaign
    //   TODO:: IN STORE Offers - LIMITED
    // | LimitedTimeFinancingOfferViewModel_SpecialCampaignOffer
    | undefined,
  monthlyOfferMessage?: Maybe<string>,
) {
  // Ensure cartTotal is a number
  cartTotal = typeof cartTotal === 'number' ? cartTotal : parseFloat(cartTotal);

  const totalAmountFinanced = parseFloat(cartTotal?.toFixed(2));

  // Calculate the monthly payment, rounded up to the nearest whole number
  let monthlyPayment = Math.ceil(totalAmountFinanced / promoMonths);

  let financingMessage = monthlyOfferMessage;

  // case for first 3 months offer if applicable
  if (campaign) {
    const adjustedAmountFinanced = totalAmountFinanced - 3;
    const adjustedMonths = promoMonths - 3;
    monthlyPayment = Math.ceil(adjustedAmountFinanced / adjustedMonths);
    if (monthlyOfferMessage) {
      financingMessage = monthlyOfferMessage.replace(
        '{financedAmount}',
        monthlyPayment.toString(),
      );
    }
  }

  return { totalAmountFinanced, financingMessage, monthlyPayment };
}

export const getCampaignRemainingCost = (
  campaign: IFinancingOfferCampaign,
  //   TODO:: IN STORE Offers - LIMITED
  // | LimitedTimeFinancingOfferViewModel_SpecialCampaignOffer,
  financingOffer: IFinancingOfferFormatted,
  cartTotalAmount: number,
): ICampaignRemainingCost | undefined => {
  const initialCost =
    (campaign?.initialMonthlyAmount ?? 0) *
    (campaign?.numOfDiscountedMonths ?? 0); // initial cost of campaign
  const remainingCost = cartTotalAmount - initialCost;
  if (!financingOffer.numOfMonths) return;
  const remainingMonths =
    financingOffer.numOfMonths - (campaign.numOfDiscountedMonths ?? 0);
  const monthlyCost = remainingCost / remainingMonths;

  return {
    cost: Math.ceil(monthlyCost),
    months: remainingMonths,
  };
};

//   TODO:: IN STORE Offers - LIMITED
// export const isValidLimitedTimeOffer = (offer: LimitedTimeFinancingOffer) => {
//   const startDate = dayjs(offer?.offerValidStartDate, synchronyDateFormat);
//   const endDate = dayjs(offer?.offerValidEndDate, synchronyDateFormat).add(
//     1,
//     'd',
//   );

//   const startDateCentralTime =
//     startDate.isValid() &&
//     dayjs(startDate.format(synchronyDateFormat), synchronyDateFormat)
//       .tz(DEFAULT_NSP_TIMEZONE)
//       .local();
//   const endDateCentralTime =
//     endDate.isValid() &&
//     dayjs(endDate.format(synchronyDateFormat), synchronyDateFormat)
//       .tz(DEFAULT_NSP_TIMEZONE)
//       .local();

//   const now = dayjs();
//   if (
//     startDateCentralTime &&
//     endDateCentralTime &&
//     now.isBetween(startDateCentralTime, endDateCentralTime)
//   ) {
//     return offer;
//   }
// };

export const isScheduled = (offer: IFinancingOfferFormatted): boolean => {
  const startDate = dayjs(offer.startDate, synchronyDateFormat);
  const endDate = dayjs(offer.endDate, synchronyDateFormat).add(1, 'd');

  const startDateCentralTime =
    startDate.isValid() &&
    dayjs(startDate.format(synchronyDateFormat), synchronyDateFormat)
      .tz('America/Chicago')
      .local();
  const endDateCentralTime =
    endDate.isValid() &&
    dayjs(endDate.format(synchronyDateFormat), synchronyDateFormat)
      .tz('America/Chicago')
      .local();

  const now = dayjs();

  return (
    startDateCentralTime &&
    endDateCentralTime &&
    now.isBetween(startDateCentralTime, endDateCentralTime)
  );
};
export const isCampaignScheduled = (
  offer: IFinancingOfferFormatted,
): boolean => {
  const tempOffer = offer;
  if (
    tempOffer.specialCampaign?.campaignValidStartDate &&
    tempOffer.specialCampaign?.campaignValidEndDate
  ) {
    tempOffer.startDate = tempOffer.specialCampaign?.campaignValidStartDate;
    tempOffer.endDate = tempOffer.specialCampaign?.campaignValidEndDate;

    return isScheduled(tempOffer);
  }

  return false;
};

export const getValidSpecialCampaign = (
  financingOffer: IFinancingOfferFormatted,
) => {
  if (
    financingOffer.specialCampaign &&
    financingOffer.specialCampaign.enableCampaign &&
    financingOffer.specialCampaign.initialMonthlyAmount &&
    financingOffer.specialCampaign.numOfDiscountedMonths &&
    isCampaignScheduled(financingOffer)
  ) {
    return financingOffer.specialCampaign;
  }

  return undefined;
};

export const extractMonthAndDate = (
  dateString: string | undefined | null,
): string | undefined => {
  if (!dateString) {
    return undefined;
  }
  try {
    const parsedDate = parse(dateString, 'M/d/yyyy', new Date());
    return format(parsedDate, 'MM/dd');
  } catch {
    // Handle parsing error, return undefined if the dateString is invalid
    return undefined;
  }
};
